// src/components/pat/PatAssistant.jsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  IconButton,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";

import {
  FiChevronDown,
  FiExternalLink,
  FiMessageCircle,
  FiRefreshCw,
  FiSend,
  FiX,
} from "react-icons/fi";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  answerPat,
  getPatIntro,
} from "./patEngine";

const BRAND_RED = "#E63946";
const BRAND_RED_DARK = "#C92A37";
const BRAND_RED_LIGHT = "#FFF5F5";
const BRAND_BORDER = "#FED7D7";

const QUICK_QUESTIONS = [
  "How much does it cost?",
  "How can this help my business?",
  "Tell me about PR4",
  "How do postcode sectors work?",
  "How does Royal Mail delivery work?",
  "What is the QR redemption page?",
  "What is QR Analytics Pro?",
  "Explain Give £10. Get £10.",
];

export default function PatAssistant() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const mountedRef = useRef(true);
  const requestRef = useRef(0);

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);

  const intro = useMemo(
    () => getPatIntro(pathname),
    [pathname]
  );

  // -------------------------------------------------------
  // MOUNT STATUS
  // -------------------------------------------------------

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // -------------------------------------------------------
  // AUTO SCROLL
  // -------------------------------------------------------

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timer = window.setTimeout(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 80);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    messages,
    isThinking,
    isOpen,
  ]);

  // -------------------------------------------------------
  // OPEN PAT
  // -------------------------------------------------------

  const openPat = () => {
    setIsOpen(true);

    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
  };

  // -------------------------------------------------------
  // RESET
  // -------------------------------------------------------

  const resetConversation = () => {
    requestRef.current += 1;

    setMessages([]);
    setInput("");
    setIsThinking(false);

    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // -------------------------------------------------------
  // SUBMIT QUESTION
  // -------------------------------------------------------

  const submitQuestion = async (
    rawQuestion
  ) => {
    const question = String(
      rawQuestion || ""
    ).trim();

    if (
      !question ||
      isThinking
    ) {
      return;
    }

    const historyForPat =
      messages.map((message) => ({
        role: message.role,
        text: message.text,
      }));

    const requestId =
      requestRef.current + 1;

    requestRef.current =
      requestId;

    const userMessage = {
      id: `${Date.now()}-user`,
      role: "user",
      text: question,
    };

    setMessages((current) => [
      ...current,
      userMessage,
    ]);

    setInput("");
    setIsThinking(true);

    // Small pause so Pat feels conversational
    // while also allowing sector lookups to run.
    await new Promise(
      (resolve) => {
        window.setTimeout(
          resolve,
          350
        );
      }
    );

    let answer;

    try {
      answer =
        await answerPat({
          question,

          messages:
            historyForPat,

          pathname,
        });
    } catch (error) {
      console.error(
        "[Pat] Answer failed:",
        error
      );

      answer = {
        text:
          "I couldn't retrieve that information just now. Please try again, or open the postcode map to check the sector directly.",

        action: {
          label:
            "Choose a Postcode Sector",

          to: "/map",
        },
      };
    }

    // Conversation may have been reset
    // while the async sector lookup was running.
    if (
      !mountedRef.current ||
      requestRef.current !==
        requestId
    ) {
      return;
    }

    const patMessage = {
      id: `${Date.now()}-pat`,
      role: "pat",

      text:
        answer?.text ||
        "I couldn't find an answer for that just now. Try asking about pricing, a postcode sector, delivery or QR redemption.",

      action:
        answer?.action ||
        null,
    };

    setMessages((current) => [
      ...current,
      patMessage,
    ]);

    setIsThinking(false);
  };

  // -------------------------------------------------------
  // FORM
  // -------------------------------------------------------

  const handleSubmit = (
    event
  ) => {
    event.preventDefault();

    submitQuestion(input);
  };

  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------

  return (
    <Box
      position="fixed"
      right={{
        base: 3,
        md: 5,
      }}
      bottom={{
        base: 3,
        md: 5,
      }}
      zIndex={1400}
      width={{
        base:
          "calc(100% - 24px)",

        sm: "390px",
      }}
      maxW="390px"
      pointerEvents="none"
    >
      {/* CHAT WINDOW */}

      <Collapse
        in={isOpen}
        animateOpacity
      >
        <Box
          pointerEvents="auto"
          mb={3}
          bg="white"
          borderWidth="1px"
          borderColor={
            BRAND_BORDER
          }
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="0 18px 50px rgba(15, 23, 42, 0.24)"
        >
          {/* HEADER */}

          <Flex
            px={4}
            py={3}
            align="center"
            justify="space-between"
            bg="gray.900"
            color="white"
          >
            <Flex
              align="center"
              gap={3}
              minW={0}
            >
              <Flex
                width="42px"
                height="42px"
                align="center"
                justify="center"
                borderRadius="full"
                bg={BRAND_RED}
                fontSize="xl"
                flexShrink={0}
              >
                👩
              </Flex>

              <Box minW={0}>
                <Flex
                  align="center"
                  gap={2}
                >
                  <Text
                    fontWeight="extrabold"
                  >
                    Ask Pat
                  </Text>

                  <Badge
                    bg="green.400"
                    color="gray.900"
                    borderRadius="full"
                    fontSize="9px"
                  >
                    ONLINE
                  </Badge>
                </Flex>

                <Text
                  color="whiteAlpha.700"
                  fontSize="xs"
                  noOfLines={1}
                >
                  Postcode.Coupons
                  assistant
                </Text>
              </Box>
            </Flex>

            <Flex
              align="center"
              gap={1}
            >
              <IconButton
                aria-label="Start a new conversation"
                title="Start again"
                icon={
                  <FiRefreshCw />
                }
                variant="ghost"
                color="white"
                size="sm"
                _hover={{
                  bg:
                    "whiteAlpha.200",
                }}
                onClick={
                  resetConversation
                }
              />

              <IconButton
                aria-label="Close Ask Pat"
                icon={<FiX />}
                variant="ghost"
                color="white"
                size="sm"
                _hover={{
                  bg:
                    "whiteAlpha.200",
                }}
                onClick={() =>
                  setIsOpen(false)
                }
              />
            </Flex>
          </Flex>

          {/* CHAT */}

          <Box
            ref={scrollRef}
            px={4}
            py={4}
            height={{
              base:
                "min(58vh, 510px)",

              sm: "510px",
            }}
            overflowY="auto"
            bg="#FCFCFD"
          >
            <VStack
              align="stretch"
              spacing={3}
            >
              {/* INTRO */}

              <Flex
                align="flex-start"
                gap={2}
              >
                <PatAvatar />

                <PatBubble>
                  <Text
                    fontWeight="bold"
                    fontSize="sm"
                    color="gray.900"
                  >
                    Hi, I'm Pat 👋
                  </Text>

                  <Text
                    mt={1}
                    color="gray.600"
                    fontSize="sm"
                    lineHeight="1.65"
                  >
                    {intro}
                  </Text>
                </PatBubble>
              </Flex>

              {/* QUICK QUESTIONS */}

              {messages.length ===
                0 && (
                <Box pt={1}>
                  <Text
                    mb={2}
                    color="gray.500"
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Popular questions
                  </Text>

                  <Flex
                    gap={2}
                    flexWrap="wrap"
                  >
                    {QUICK_QUESTIONS.map(
                      (
                        question
                      ) => (
                        <Button
                          key={
                            question
                          }
                          size="xs"
                          height="auto"
                          py={2}
                          px={3}
                          whiteSpace="normal"
                          textAlign="left"
                          bg={
                            BRAND_RED_LIGHT
                          }
                          color={
                            BRAND_RED_DARK
                          }
                          borderWidth="1px"
                          borderColor={
                            BRAND_BORDER
                          }
                          borderRadius="full"
                          isDisabled={
                            isThinking
                          }
                          _hover={{
                            bg:
                              "#FFE9EA",
                          }}
                          onClick={() =>
                            submitQuestion(
                              question
                            )
                          }
                        >
                          {question}
                        </Button>
                      )
                    )}
                  </Flex>
                </Box>
              )}

              {/* CONVERSATION */}

              {messages.map(
                (message) => {
                  if (
                    message.role ===
                    "user"
                  ) {
                    return (
                      <Flex
                        key={
                          message.id
                        }
                        justify="flex-end"
                      >
                        <Box
                          maxW="86%"
                          px={3}
                          py={2.5}
                          bg={
                            BRAND_RED
                          }
                          color="white"
                          borderRadius="14px 0 14px 14px"
                        >
                          <MessageText
                            text={
                              message.text
                            }
                          />
                        </Box>
                      </Flex>
                    );
                  }

                  return (
                    <Flex
                      key={
                        message.id
                      }
                      align="flex-start"
                      gap={2}
                    >
                      <PatAvatar />

                      <Box
                        maxW="86%"
                      >
                        <PatBubble>
                          <MessageText
                            text={
                              message.text
                            }
                          />
                        </PatBubble>

                        {message.action && (
                          <Button
                            mt={2}
                            size="xs"
                            rightIcon={
                              <FiExternalLink />
                            }
                            colorScheme="red"
                            variant="outline"
                            onClick={() => {
                              navigate(
                                message
                                  .action
                                  .to
                              );

                              setIsOpen(
                                false
                              );
                            }}
                          >
                            {
                              message
                                .action
                                .label
                            }
                          </Button>
                        )}
                      </Box>
                    </Flex>
                  );
                }
              )}

              {/* THINKING / LIVE LOOKUP */}

              {isThinking && (
                <Flex
                  align="flex-start"
                  gap={2}
                >
                  <PatAvatar />

                  <PatBubble>
                    <Flex
                      align="center"
                      gap={1}
                    >
                      <ThinkingDot
                        delay="0s"
                      />

                      <ThinkingDot
                        delay="0.15s"
                      />

                      <ThinkingDot
                        delay="0.3s"
                      />

                      <Text
                        ml={2}
                        color="gray.500"
                        fontSize="xs"
                      >
                        Pat is checking…
                      </Text>
                    </Flex>
                  </PatBubble>
                </Flex>
              )}
            </VStack>
          </Box>

          {/* INPUT */}

          <Box
            px={3}
            py={3}
            bg="white"
            borderTopWidth="1px"
            borderColor="gray.100"
          >
            <form
              onSubmit={
                handleSubmit
              }
            >
              <Flex gap={2}>
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(
                    event
                  ) =>
                    setInput(
                      event.target
                        .value
                    )
                  }
                  placeholder="Ask Pat anything..."
                  autoComplete="off"
                  maxLength={500}
                  bg="gray.50"
                  borderColor="gray.200"
                  isDisabled={
                    isThinking
                  }
                  _focus={{
                    borderColor:
                      BRAND_RED,

                    boxShadow:
                      `0 0 0 1px ${BRAND_RED}`,

                    bg: "white",
                  }}
                />

                <IconButton
                  type="submit"
                  aria-label="Send message to Pat"
                  icon={<FiSend />}
                  bg={BRAND_RED}
                  color="white"
                  flexShrink={0}
                  isDisabled={
                    !input.trim() ||
                    isThinking
                  }
                  _hover={{
                    bg:
                      BRAND_RED_DARK,
                  }}
                />
              </Flex>
            </form>

            <Text
              mt={2}
              color="gray.400"
              textAlign="center"
              fontSize="10px"
            >
              Pat uses
              Postcode.Coupons
              information. Live
              sector data depends
              on current platform
              records. Check
              checkout and the
              Terms before
              purchasing.
            </Text>
          </Box>
        </Box>
      </Collapse>

      {/* FLOATING BUTTON */}

      <Flex
        justify="flex-end"
        pointerEvents="auto"
      >
        <Button
          size="lg"
          height="54px"
          px={5}
          leftIcon={
            <FiMessageCircle />
          }
          rightIcon={
            isOpen ? (
              <FiChevronDown />
            ) : undefined
          }
          bg={BRAND_RED}
          color="white"
          borderRadius="full"
          boxShadow="0 10px 30px rgba(230, 57, 70, 0.35)"
          fontWeight="extrabold"
          _hover={{
            bg:
              BRAND_RED_DARK,

            transform:
              "translateY(-1px)",

            boxShadow:
              "0 12px 34px rgba(230, 57, 70, 0.4)",
          }}
          _active={{
            transform:
              "translateY(0)",
          }}
          onClick={() =>
            isOpen
              ? setIsOpen(false)
              : openPat()
          }
        >
          👩 Ask Pat
        </Button>
      </Flex>
    </Box>
  );
}

// -------------------------------------------------------
// PAT AVATAR
// -------------------------------------------------------

function PatAvatar() {
  return (
    <Flex
      width="30px"
      height="30px"
      align="center"
      justify="center"
      borderRadius="full"
      bg={BRAND_RED_LIGHT}
      flexShrink={0}
    >
      👩
    </Flex>
  );
}

// -------------------------------------------------------
// PAT BUBBLE
// -------------------------------------------------------

function PatBubble({
  children,
}) {
  return (
    <Box
      maxW="100%"
      px={3}
      py={2.5}
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="0 14px 14px 14px"
      boxShadow="xs"
    >
      {children}
    </Box>
  );
}

// -------------------------------------------------------
// MESSAGE TEXT
// -------------------------------------------------------

function MessageText({
  text,
}) {
  const paragraphs =
    String(text || "")
      .split("\n")
      .map(
        (paragraph) =>
          paragraph.trim()
      )
      .filter(Boolean);

  return (
    <VStack
      align="stretch"
      spacing={2}
    >
      {paragraphs.map(
        (
          paragraph,
          index
        ) => (
          <Text
            key={`${index}-${paragraph}`}
            fontSize="sm"
            lineHeight="1.65"
          >
            {paragraph}
          </Text>
        )
      )}
    </VStack>
  );
}

// -------------------------------------------------------
// THINKING DOT
// -------------------------------------------------------

function ThinkingDot({
  delay,
}) {
  return (
    <Box
      width="7px"
      height="7px"
      borderRadius="full"
      bg={BRAND_RED}
      animation="patPulse 1s infinite ease-in-out"
      sx={{
        animationDelay:
          delay,

        "@keyframes patPulse":
          {
            "0%, 100%": {
              opacity: 0.3,

              transform:
                "translateY(0)",
            },

            "50%": {
              opacity: 1,

              transform:
                "translateY(-3px)",
            },
          },
      }}
    />
  );
}